import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import { useParams } from "react-router-dom";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Fab,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import { format } from "date-fns";
import { da, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useChild } from "../../context/ChildContext";
import AddIcon from "@mui/icons-material/Add";
import DiaryForm from "./DiaryForm";

export default function DiaryList() {
  const { childId } = useParams();
  const { i18n } = useTranslation();
  const dateFnsLocale = i18n.language === "da" ? da : enUS;
  const { currentChild, getChildAgeAtEntry } = useChild();
  const [entries, setEntries] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [imageDialogUrl, setImageDialogUrl] = useState(null);

  useEffect(() => {
    if (!childId) return;
    const q = query(
      collection(db, `children/${childId}/diary`),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q, (snapshot) => {
      setEntries(
        snapshot.docs.map((d) => ({
          id: d.id,
          text: d.data().text || "",
          imageUrl: d.data().imageUrl || null,
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
        })),
      );
    });
  }, [childId]);

  const handleDelete = async () => {
    if (!editingEntry) return;
    const confirm = window.confirm(
      "Are you sure you want to delete this entry?",
    );
    if (!confirm) return;
    if (editingEntry.imageUrl) {
      try {
        await deleteObject(ref(storage, editingEntry.imageUrl));
      } catch {}
    }
    await deleteDoc(doc(db, `children/${childId}/diary/${editingEntry.id}`));
    setEditingEntry(null);
  };

  return (
    <Stack spacing={2}>
      {entries.length === 0 && (
        <Typography
          variant="body1"
          align="center"
        >
          No diary entries yet.
        </Typography>
      )}

      {entries.map((entry) => (
        <Paper
          key={entry.id}
          elevation={10}
          sx={{ bgcolor: "background.card", borderRadius: 2, p: 2, cursor: "pointer" }}
          onClick={() => setEditingEntry(entry)}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {format(entry.createdAt, "PPP", { locale: dateFnsLocale })}
            </Typography>
            <Chip
              label={
                currentChild
                  ? getChildAgeAtEntry(currentChild.birthDate, entry.createdAt)
                  : null
              }
              size="small"
            />
          </Box>
          <Typography
            variant="h5"
            gutterBottom
          >
            {entry.text}
          </Typography>
          {entry.imageUrl && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setImageDialogUrl(entry.imageUrl);
              }}
            >
              <img
                src={entry.imageUrl}
                alt="Diary"
                style={{ maxWidth: "100%", borderRadius: 8, cursor: "pointer" }}
              />
            </Box>
          )}
        </Paper>
      ))}

      <Fab
        aria-label="add"
        onClick={() => setIsCreateOpen(true)}
        sx={{
          position: "fixed",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: darken("#7AA490", 0.2),
          "&:hover": { backgroundColor: darken("#7AA490", 0.3) },
          color: "white",
        }}
      >
        <AddIcon />
      </Fab>

      <Dialog
        open={!!imageDialogUrl}
        onClose={() => setImageDialogUrl(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ p: 0 }}>
          <img
            src={imageDialogUrl}
            alt="Diary"
            style={{ display: "block", maxWidth: "100%", maxHeight: "90vh" }}
          />
        </DialogContent>
      </Dialog>

      <DiaryForm
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <DiaryForm
        open={!!editingEntry}
        onClose={() => setEditingEntry(null)}
        entry={editingEntry}
        onDelete={handleDelete}
      />
    </Stack>
  );
}
